import React, { useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
import { ENABLE_REGISTRATION, ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { useHistory } from 'react-router-dom'
import { connect } from 'react-redux'

import Page from '../../components/Page'
import FormSheet from '../../components/Form/FormSheet'
import { feedItemForm, teamForm } from '../../components/Form/form-configs'
import UserList, { AddUser } from '../../components/List/UserList'
import FavouriteButton from '../../components/Page/FavouriteButton'
import PlayerList from '../../components/List/PlayerList'
import TeamOfficialList, {
  AddTeamOfficial,
} from '../../components/List/TeamOfficialList'
import GameList from '../../components/List/GameList'
import Statistics from '../../components/Statistics'
import NewButton from '../../components/Page/NewButton'
import FileDownload from '../../components/FileDownload'
import BaseActionButton from '../../components/Page/BaseActionButton'
import { Mobile } from '../../components/Responsive'
import EditButton from '../../components/Page/EditButton'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import ImportTeamRoster from '../../components/ImportCSV/ImportTeamRoster'
import { getBreadcrumbs, useReadItem, useStoreRecent } from '../utils'
import Home from '../Home'
import EditEntitySubTitle from '../EditEntitySubTitle'
import CreatePlayerSheet from './CreatePlayerSheet'
import FinancialList from '../../components/List/FinancialList'
import {
  getHockeySport,
  getSoccerSport,
  getTeamOfficialTypesForHockey,
  getTeamOfficialTypesForSoccer,
} from '@sportninja/common/selectors/types'

const Team = ({
  match,
  isInFrame,
  hockeySportId,
  soccerSportId,
  teamOfficialTypesForHockey,
  teamOfficialTypesForSoccer,
}) => {
  const history = useHistory()
  const [rosterId, setRosterId] = useState('')
  const [players, setPlayers] = useState([])
  const [creatingPlayersRequiresEmail, setCreatingPlayersRequiresEmail] =
    useState(false)
  const [areFiltersHidden, setAreFiltersHidden] = useState(false)
  const [filterForExporting, setFilterForExporting] = useState(null)

  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
  } = useReadItem()

  const TEAM_ID = match.params.id
  const BASE_ROUTE = `${ROUTES.TEAM_ROOT}/${TEAM_ID}`
  const IS_PUBLIC = item.is_public

  useStoreRecent(ENTITY_TYPES.team, item, error, TEAM_ID, isInFrame)

  useEffect(() => {
    readItem(ENTITY_TYPES.team, TEAM_ID)
  }, [TEAM_ID])

  const onEditTeam = async (values) => {
    const { imageData, ...form } = values

    if (form?.name_full) form.name = form.name_full

    if (form.sport_type) {
      if (form.sport_type === 'Ice Hockey') {
        form.sport_id = hockeySportId
      }
      if (form.sport_type === 'Soccer') {
        form.sport_id = soccerSportId
      }
    } else {
      form.sport_id = hockeySportId
    }

    if (form?.delete_image) {
      await req(`/teams/${item.id}/image`, {
        method: 'DELETE',
      })
    }

    const body = JSON.stringify(form)
    const response = await req(`/teams/${item.id}`, {
      method: 'PUT',
      body,
    })
    if (imageData) {
      await req(`/teams/${item.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }
    await readItem(ENTITY_TYPES.team, TEAM_ID)
    return response
  }

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
        />,
        permission.update && (
          <FormSheet
            customDeleteModal={
              permission.delete && (
                <EntityFormDelete
                  entityType={ENTITY_TYPES.team}
                  itemId={TEAM_ID}
                  title={`Delete ${item.name_full}`}
                  type={t('common:team')}
                  onComplete={() => {
                    history.push(`${ROUTES.TEAMS.ROOT}`)
                  }}
                />
              )
            }
            key='edit'
            Button={({ onClick }) => (
              <EditButton onClick={onClick} title={item?.name_full} />
            )}
            form={teamForm(
              parsers[ENTITY_TYPES.team]({ ...item, permission }),
              false,
              hockeySportId
            )}
            label={t('Web:editTeam')}
            onSubmit={onEditTeam}
            title={t('Web:editTeam')}
            subTitle={
              <EditEntitySubTitle id={TEAM_ID} entityType={ENTITY_TYPES.team} />
            }
          />
        ),
      ]
    : []

  const baseActions = []

  const filterToggleButton = (
    <BaseActionButton
      faType={areFiltersHidden ? 'far' : 'fas'}
      key='toggle-filter'
      name='filter'
      title={`Toggle Filters Visibility ${areFiltersHidden ? 'on' : 'off'}`}
      onClick={() => {
        setAreFiltersHidden((v) => !v)
      }}
    />
  )

  const pages = [
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/schedule`,
      text: t('common:schedule'),
      component: (
        <GameList
          team_id={TEAM_ID}
          areFiltersHidden={areFiltersHidden}
          setFilterForExporting={setFilterForExporting}
        />
      ),
      actions: [filterToggleButton],
    },
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/statistics`,
      text: t('common:statistics'),
      component: (
        <Statistics
          defaultSort='GP'
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          areFiltersHidden={areFiltersHidden}
          autoSelectMostRecentComp
        />
      ),
      actions: [filterToggleButton],
    },
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/roster`,
      text: t('common:roster'),
      component: (
        <PlayerList
          refreshKey={refreshKey}
          teamId={TEAM_ID}
          teamName={item?.name_full}
          permission={permission}
          onComplete={(data) => {
            setRosterId(data.id)
            setPlayers(data.players)
            setCreatingPlayersRequiresEmail(data.require_email)
          }}
          shouldShowWaiverStatus={item?.should_show_waiver}
          isSoccer={item?.sport?.id === soccerSportId}
          sportName={item?.sport?.name}
        />
      ),
      actions: [
        permission.update && rosterId && (
          <FileDownload
            key='download-roster'
            button={(loading, onClick) => {
              return (
                <BaseActionButton
                  loading={loading}
                  onClick={onClick}
                  name='download'
                  title='Export Roster'
                />
              )
            }}
            label={t('common:exportRoster')}
            url={`/teams/${TEAM_ID}/rosters/${rosterId}/players/export/csv`}
            fileName={item.name_full}
            query={filterForExporting}
          />
        ),
        permission.update && rosterId && (
          <ImportTeamRoster
            key='import-roster'
            teamId={TEAM_ID}
            teamName={item.name_full}
            teamRosterId={rosterId}
            onSuccess={triggerRefresh}
          />
        ),
        permission.update && rosterId && item.organization && (
          <CreatePlayerSheet
            key='add-player'
            teamId={TEAM_ID}
            orgId={item.organization.id}
            orgName={item.organization.name_full}
            rosterId={rosterId}
            players={players}
            hasAdminPermission={permission?.admin}
            creatingPlayersRequiresEmail={creatingPlayersRequiresEmail}
            onSuccess={triggerRefresh}
            sportName={item?.sport?.name}
          />
        ),
      ],
    },
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/feed`,
      text: t('common:feed'),
      component: (
        <Home
          isEntityFeed
          entity={item}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          refreshKey={refreshKey}
          hideSidebar={!permission.update}
        />
      ),
      actions: [
        permission.admin && (
          <Mobile key='add-post'>
            <FormSheet
              label={t('Web:createANewPost')}
              title={t('Web:createANewPost')}
              form={feedItemForm({}, false)}
              onSubmit={async ({ imageData, ...form }) => {
                const body = JSON.stringify(form)
                const response = await req(`/teams/${TEAM_ID}/social-feed`, {
                  method: 'POST',
                  body,
                })
                if (imageData) {
                  await req(`/social-feed/${response.data.id}/image`, {
                    method: 'POST',
                    body: imageData,
                  })
                }
                triggerRefresh()
              }}
              Button={({ onClick }) => <NewButton onClick={onClick} />}
            />
          </Mobile>
        ),
      ],
    },
    !isInFrame &&
      ENABLE_REGISTRATION && {
        to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/billing`,
        text: t('common:billing'),
        privatePage: false,
        component: (
          <FinancialList canEdit={permission.update} teamId={TEAM_ID} />
        ),
        actions: [],
      },
    !isInFrame &&
      permission.update && {
        to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/team-officials`,
        text: t('common:teamOfficial', { count: 2 }),
        privatePage: true,
        component: (
          <TeamOfficialList
            key='team-officials'
            teamId={TEAM_ID}
            teamName={item?.name_full}
            permission={permission}
            onComplete={(data) => {
              setRosterId(data.id)
            }}
            refreshKey={refreshKey}
            sportName={item?.sport?.name}
          />
        ),
        actions: [
          rosterId && (
            <AddTeamOfficial
              key='add-team-official'
              teamId={TEAM_ID}
              rosterId={rosterId}
              permission={permission}
              onComplete={triggerRefresh}
              sportName={item?.sport?.name}
              teamOfficialTypesForHockey={teamOfficialTypesForHockey}
              teamOfficialTypesForSoccer={teamOfficialTypesForSoccer}
            />
          ),
        ],
      },
    !isInFrame &&
      permission.update && {
        to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/users`,
        text: t('common:user', { count: 2 }),
        privatePage: true,
        component: (
          <UserList
            key='users'
            entityType={ENTITY_TYPES.team}
            entityId={TEAM_ID}
            slug={`/teams/${TEAM_ID}/permissions`}
            refreshKey={refreshKey}
          />
        ),
        actions: [
          permission.admin && (
            <AddUser
              key='add-user'
              entityType={ENTITY_TYPES.team}
              entityId={TEAM_ID}
              permission={permission}
              onComplete={triggerRefresh}
            />
          ),
        ],
      },
  ]

  return (
    <Page
      isTeamOrSchedule
      error={error}
      loading={loading}
      baseCrumb={
        error
          ? ROUTES.TEAMS.ROOT
          : `${ROUTES.ORG_ROOT}/${item?.organization?.id}/teams`
      }
      baseRoute={BASE_ROUTE}
      baseTitle={item?.organization?.name_full}
      breadcrumbs={getBreadcrumbs(ROUTES.TEAM_ROOT, item, hierarchy)}
      baseActions={baseActions}
      pages={pages}
      isPrivate={!IS_PUBLIC}
      title={item.name_full}
      iconName='user-friends'
      imageId={getImageThumbnailId(item)}
      titleActions={titleActions}
    />
  )
}

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
    hockeySportId: getHockeySport(state)?.id,
    soccerSportId: getSoccerSport(state)?.id,
    hockeySport: getHockeySport(state),
    soccerSport: getSoccerSport(state),
    teamOfficialTypesForHockey: getTeamOfficialTypesForHockey(state),
    teamOfficialTypesForSoccer: getTeamOfficialTypesForSoccer(state),
  }
}

export default connect(mapStateToProps)(Team)
